/* eslint-disable no-console */
import {PlusOutlined, MinusOutlined} from '@ant-design/icons';
import {Card, Button, Table, Tag, Typography} from 'antd';
import type {ColumnType} from 'antd/es/table';
import React, {useState} from 'react';

import envIcon from '../../../../assets/environmental.png';
import govIcon from '../../../../assets/governance.png';
import socialIcon from '../../../../assets/social.png';
import Loader from '../../../../components/Loader/Loader';
import type {
  GapAnalysisMetricRow,
  GapAnalysisPillarView,
  PeerMetricRow,
  PeerCompanyCell as PeerCompanyCellData
} from '../../../../services/modules/ClimateDB/gapAnalysisAPI';
import useCompanyStore from '../../../../store/companyStore/companyDetailsStore';
import {OPKey} from '../../../configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';
const {Text, Title} = Typography;
// sub_category_changes
interface ITextColor {
  text: string;
  color: string;
}

const SECTION_IMAGE: Record<string, string> = {
  environmental: envIcon,
  env: envIcon,
  social: socialIcon,
  governance: govIcon,
  gov: govIcon,
  strategy: socialIcon
};

const SUBJECT_COLUMN_BG = 'rgb(224, 239, 253)';

const PeerCell: React.FC<{value?: string}> = ({value}) => (
  <div
    style={{
      whiteSpace: 'normal',
      textAlign: 'left',
      minWidth: 160,
      maxWidth: 280
    }}
  >
    {value ?? '—'}
  </div>
);

interface PeerTableRecord {
  key: string;
  metric: string;
  [cellKey: string]: string | PeerCompanyCellData | undefined;
}

const transformPeerDataSourceForTable = (
  datasource: PeerMetricRow[]
): PeerTableRecord[] => {
  return datasource.map((row) => {
    const record: PeerTableRecord = {
      key: row.key,
      metric: row.metric,
      average: row.average
    };

    // Add cell data for each company
    row.cells?.forEach((cell) => {
      record[`cell_${cell.entity_id}`] = cell;
    });

    return record;
  });
};

const GapAnalysisSection: React.FC<{
  isPeerTab?: boolean;
  livePillars?: GapAnalysisPillarView[] | null;
  isGapLoading?: boolean;
  isGapUnavailable?: boolean;
}> = ({
  isPeerTab = false,
  livePillars = null,
  isGapLoading = false,
  isGapUnavailable = false
}) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const companyDetails = useCompanyStore((state) => state.companyDetails);
  const usingLiveData = livePillars !== null;

  const metricColumn: any = {
    title: 'ESG Metric',
    dataIndex: 'metric',
    key: 'metric',
    fixed: 'left',
    width: 200
  };

  const industryAverageColumn: any = {
    title: 'Industry Average',
    dataIndex: 'average',
    key: 'average',
    fixed: isPeerTab ? undefined : 'left',
    render: (obj: ITextColor) => {
      if (isPeerTab) {
        return <PeerCell value={obj?.text} />;
      }

      return <Tag color={obj?.color}>{obj?.text}</Tag>;
    }
  };

  // Generate columns for peer comparison
  const peerIndustryAverageColumn: ColumnType<PeerTableRecord> = {
    title: 'Industry Average',
    dataIndex: 'average',
    key: 'average',
    width: 160,
    render: (value: string) => <PeerCell value={value} />
  };

  const getPeerComparisonColumns = (
    section: GapAnalysisPillarView
  ): Array<ColumnType<PeerTableRecord>> => {
    const companies = section.companies ?? [];
    const companyColumns: Array<ColumnType<PeerTableRecord>> = companies.map(
      (company) => ({
        title: (
          <div
            style={{textAlign: 'center', fontSize: '12px', fontWeight: '600'}}
          >
            {company.legal_name}
          </div>
        ),
        dataIndex: `cell_${company.entity_id}`,
        key: `cell_${company.entity_id}`,
        align: 'center' as const,
        width: 120,
        ...(company.is_subject
          ? {
              fixed: 'left' as const,
              onHeaderCell: () => ({
                style: {backgroundColor: SUBJECT_COLUMN_BG}
              }),
              onCell: () => ({
                style: {
                  verticalAlign: 'top',
                  backgroundColor: SUBJECT_COLUMN_BG
                }
              })
            }
          : {}),
        render: (_: unknown, record: PeerTableRecord) => {
          const cell = record[`cell_${company.entity_id}`];

          return cell && typeof cell === 'object' ? (
            <PeerCell value={cell.display} />
          ) : (
            <div style={{textAlign: 'center'}}>—</div>
          );
        }
      })
    );

    // Metric and subject (user's own) company columns stay fixed; peers scroll, Industry Average last.
    const subjectColumns = companyColumns.filter(
      (_, index) => companies[index].is_subject
    );
    const peerColumns = companyColumns.filter(
      (_, index) => !companies[index].is_subject
    );

    return [
      metricColumn,
      ...subjectColumns,
      ...peerColumns,
      peerIndustryAverageColumn
    ].map((column) => ({
      onCell: () => ({style: {verticalAlign: 'top'}}),
      ...column
    }));
  };

  const columns: any =
    isPeerTab && livePillars?.[0]?.isPeerComparison
      ? [] // Will be set per section
      : isPeerTab
        ? [
            metricColumn,
            {
              title: <div style={{textAlign: 'center'}}>Gap Analysis</div>,
              align: 'center',
              children: [
                {
                  title: (
                    <div style={{textAlign: 'center'}}>
                      {companyDetails?.[OPKey.COMPANY_NAME] || 'Your Company'}
                    </div>
                  ),
                  dataIndex: 'current_company_gap',
                  key: 'current_company_gap',
                  align: 'center',
                  onHeaderCell: () => ({
                    style: {backgroundColor: 'rgb(224, 239, 253)'}
                  }),
                  onCell: () => ({
                    style: {backgroundColor: 'rgb(224, 239, 253)'}
                  }),
                  render: (value: string) => <PeerCell value={value} />
                }
              ]
            },
            industryAverageColumn
          ]
        : [
            metricColumn,
            industryAverageColumn,
            {
              title: 'Gap Analysis',
              dataIndex: 'gap',
              key: 'gap'
            },
            {
              title: 'Insights',
              dataIndex: 'insight',
              key: 'insight'
            }
          ];

  const sections = (livePillars ?? []).map((pillar) => ({
    ...pillar,
    image: SECTION_IMAGE[pillar.key] ?? socialIcon
  }));

  if (isGapLoading) {
    return (
      <div style={{minHeight: 480}}>
        <Loader isHome={true} />
      </div>
    );
  }

  if (isGapUnavailable) {
    return (
      <div
        style={{
          padding: '24px',
          color: '#404252',
          font: 'normal normal 600 14px Montserrat'
        }}
      >
        Gap analysis data is unavailable for this year.
      </div>
    );
  }

  if (!usingLiveData || !sections.length) {
    return (
      <div
        style={{
          padding: '24px',
          color: '#404252',
          font: 'normal normal 600 14px Montserrat'
        }}
      >
        No gap analysis data for this year.
      </div>
    );
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
      {sections.map((section) => {
        const isExpanded = expandedKey === section.key;

        return (
          <Card
            key={section.key}
            bordered
            style={{borderRadius: 8, border: '1px solid #dee2e6'}}
            bodyStyle={{padding: 16}}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{display: 'flex', gap: 16, alignItems: 'center'}}>
                <img
                  src={section.image}
                  alt={section.title}
                  style={{width: 'auto', height: 80, borderRadius: 8}}
                />
                <div>
                  <Title level={5} style={{margin: 0, fontWeight: '700'}}>
                    {section.title}
                  </Title>
                  <Text type="secondary" style={{fontWeight: '700'}}>
                    Compared to{' '}
                  </Text>
                  <span style={{fontWeight: 'bold'}}>
                    {section.metrics} Metrics
                  </span>
                </div>
              </div>

              <Button
                type="default"
                shape="circle"
                icon={
                  isExpanded ? (
                    <MinusOutlined style={{fontSize: '20px'}} />
                  ) : (
                    <PlusOutlined style={{fontSize: '20px'}} />
                  )
                }
                onClick={() => {
                  setExpandedKey(isExpanded ? null : section.key);
                }}
                style={{
                  backgroundColor: '#6C9C5A',
                  color: 'white',
                  height: '50px',
                  width: '50px'
                }}
              />
            </div>

            {isExpanded && (
              <div style={{marginTop: 20}}>
                {section.isPeerComparison ? (
                  <Table<PeerTableRecord>
                    dataSource={transformPeerDataSourceForTable(
                      section.datasource as PeerMetricRow[]
                    )}
                    columns={getPeerComparisonColumns(section)}
                    pagination={false}
                    size="middle"
                    scroll={{x: 'max-content'}}
                  />
                ) : (
                  <Table
                    dataSource={section.datasource as GapAnalysisMetricRow[]}
                    columns={columns}
                    pagination={false}
                    size="middle"
                    scroll={{x: 'max-content'}}
                  />
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default GapAnalysisSection;
